import React, { useState } from "react";
import {
  Stack,
  Divider,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SxProps,
  Icon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  useTheme,
} from "@mui/material";
import { ExpandMore, Update } from "@mui/icons-material";
import { equals } from "ramda";

import { PricingCardType } from "../../config/types";
import checkboxIcon from "../../assets/check.png";

export type CardProps = {
  cardData: PricingCardType;
  sx?: SxProps;
};

export const Card: React.FC<CardProps> = (props) => {
  const { cardData, sx = {} } = props;

  const theme = useTheme();
  const isMedium = theme.breakpoints.down("md");

  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChangeExpended =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Stack
      width={1}
      direction="column"
      justifyContent="flex-start"
      alignItems="center"
      zIndex={999}
      gap={2}
      sx={{
        width: 1,
        ...sx,
      }}
    >
      <Typography
        color="textSecondary"
        fontWeight={300}
        sx={{
          fontSize: {
            md: "2.5rem",
            xs: "1.8rem",
          },
        }}
      >
        {cardData.plan}
      </Typography>

      <Stack
        width={1}
        direction="column"
        justifyContent="flex-start"
        alignItems="center"
        position="relative"
        gap={1}
        sx={{
          border: 2,
          borderColor: "primary.dark",
          borderRadius: 5,
          padding: { md: 4, xs: 2 },
          backgroundColor: "#0b1011",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -18,
            left: "50%",
            transform: "translateX(-50%)",
            width: 280,
            height: 36,
            background:
              "radial-gradient(closest-side, rgba(120,255,200,.75), rgba(120,255,200,0) 70%)",
            filter: "blur(2px)",
            zIndex: 1,
          },

          "&::after": {
            display: { md: "block", xs: "none" },
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: -120,
            height: 36,
            background:
              "radial-gradient(closest-side, rgba(255,255,255,.6), rgba(255,255,255,0) 100%)",
            zIndex: 0,
          },
        }}
      >
        {!!cardData.badge && (
          <Chip
            label={cardData.badge}
            color="primary"
            variant="outlined"
            sx={{
              textTransform: "uppercase",
              alignSelf: "flex-end",
              borderRadius: 2,
            }}
          />
        )}

        <Typography
          width={1}
          color="textSecondary"
          fontWeight={300}
          sx={{
            fontSize: {
              md: "1.785rem",
              xs: "1.375rem",
            },
          }}
        >
          {cardData.title}
        </Typography>

        <Typography
          color="textPrimary"
          fontWeight={300}
          sx={{
            fontSize: {
              md: "1rem",
              xs: "0.875rem",
            },
          }}
        >
          {cardData.subtitle}
        </Typography>

        <Divider
          flexItem
          sx={{
            borderColor: "secondary.dark",
          }}
        />

        <Typography
          width={1}
          color="primary.light"
          fontWeight={300}
          sx={{
            fontSize: {
              md: "1.5rem",
              xs: "1.2rem",
            },
          }}
        >
          {cardData.price}
        </Typography>

        {cardData.features.map((featureItem, index) => (
          <Box width={1} key={`${featureItem.title}-${index}`}>
            <Accordion
              expanded={expanded === featureItem.title}
              onChange={handleChangeExpended(featureItem.title)}
              sx={{
                backgroundColor: "transparent",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  padding: 0,
                  minHeight: "unset",
                  "&.Mui-expanded": {
                    minHeight: "unset",
                  },
                  "& .MuiTypography-root": {
                    fontSize: {
                      md: "1rem",
                      xs: "0.7rem",
                    },
                  },
                  "& > .MuiAccordionSummary-content": {
                    margin: 0,
                  },
                }}
              >
                <Stack
                  width={1}
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  gap={1}
                >
                  <Icon
                    sx={{
                      fontSize: 20,
                    }}
                  >
                    <img width="100%" src={checkboxIcon} alt="Checkbox" />
                  </Icon>

                  <Typography color="textSecondary">
                    {featureItem.title}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>{featureItem.description}</AccordionDetails>
            </Accordion>
          </Box>
        ))}

        {!!cardData.upcomingTitle && (
          <Stack
            width={1}
            direction="column"
            justifyContent="flex-start"
            alignItems="center"
            sx={{
              gap: { md: 1, xs: 0.5 },
            }}
          >
            <Typography
              color="primary.light"
              fontWeight={700}
              sx={{
                fontSize: {
                  md: "1.2rem",
                  xs: "1rem",
                },
              }}
            >
              {cardData.upcomingTitle}
            </Typography>

            <Typography
              variant="caption"
              color="textPrimary"
              textAlign="center"
              fontWeight={300}
              sx={{
                fontSize: {
                  md: "0.875rem",
                  xs: "0.675rem",
                },
              }}
            >
              {cardData.upcomingSubtitle}
            </Typography>

            <Divider
              flexItem
              sx={{
                borderColor: "secondary.dark",
              }}
            />

            <List
              dense
              disablePadding
              sx={{
                padding: 0,
              }}
            >
              {cardData.upcomingItems.map((upcomingItem, i) => (
                <ListItem
                  key={i}
                  alignItems="flex-start"
                  sx={{ paddingY: { md: 1, xs: 0 }, paddingX: 0 }}
                >
                  <ListItemIcon sx={{ minWidth: { md: 30, xs: 24 }, mt: 0.25 }}>
                    {upcomingItem.icon && (
                      <Update
                        fontSize="small"
                        sx={{
                          color: "common.white",
                        }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle1"
                        color="textSecondary"
                        fontWeight={300}
                        sx={{
                          fontSize: {
                            md: "1rem",
                            xs: "0.875rem",
                          },
                        }}
                      >
                        {upcomingItem.title}
                      </Typography>
                    }
                    secondary={
                      upcomingItem.description && (
                        <Typography
                          variant="caption"
                          color="textPrimary"
                          sx={{
                            fontSize: {
                              md: "0.875rem",
                              xs: "0.675rem",
                            },
                          }}
                        >
                          {upcomingItem.description}
                        </Typography>
                      )
                    }
                    sx={{
                      marginTop: 0,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>
        )}

        <Divider
          flexItem
          sx={{
            borderColor: "secondary.dark",
            marginBottom: { md: 2, xs: 1 },
          }}
        />

        <Button
          variant="contained"
          color={equals(cardData.plan, "Enterprise") ? "inherit" : "primary"}
          onClick={cardData.ctaAction}
          size={isMedium ? "large" : "small"}
          sx={{
            fontWeight: 700,
            fontSize: { md: "1rem", xs: "0.875rem" },
            color: "secondary.dark",
          }}
        >
          {cardData.ctaLabel}
        </Button>

        <Typography
          color="textPrimary"
          variant="subtitle2"
          textAlign="center"
          sx={{
            fontSize: {
              md: "0.875rem",
              xs: "0.675rem",
            },
          }}
        >
          {cardData.footerNote}
        </Typography>
      </Stack>
    </Stack>
  );
};
